import { Box } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Link,
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useFetch from '../../api/useFetch';
import { useConfig } from '../../api/api';
import { ArticleSkeleton } from './ArticleSkeleton';
import {
  extractArticleOutline,
  extractMediaPlaceholderIds,
  groupOutline,
  resolveMediaPlaceholders,
  sanitizeDocumentationHtml,
} from './articleHtml';
import { formatEntryMeta } from './entryMeta';
import { labelForRole } from './roleLabels';
import { DocIcon } from './DocIcon';
import { FeedbackWidget } from './FeedbackWidget';
import { NavToggleButton } from './NavToggleButton';
import { Toc } from './Toc';
import { useCourseProgress } from './useCourseProgress';
import { mediaUuid, normalizeMedia } from './media';
import { attachWatchTracker, isTrackableVideo } from './watchTracking';
import { sendWatchReport } from './useWatchTracker';
import {
  DocumentationFileLink,
  DocumentationImage,
  DocumentationVideo,
} from './MediaBlocks';
import { ImageLightbox } from './ImageLightbox';

// Learn plan and bookmarks are not implemented yet — their buttons stay hidden until then
const SHOW_LEARN_PLAN_AND_BOOKMARKS = false;

const readList = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key)) || [];
  } catch (e) {
    return [];
  }
};

const toggleListEntry = (key, slug) => {
  const list = readList(key);
  const next = list.includes(slug)
    ? list.filter((item) => item !== slug)
    : [...list, slug];
  localStorage.setItem(key, JSON.stringify(next));
  return next.includes(slug);
};

const actionButtonSx = (primary) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '7px',
  border: primary ? 'none' : '1px solid var(--border)',
  background: primary ? 'var(--accent)' : 'var(--panel)',
  color: primary ? '#ffffff' : 'var(--text)',
  fontFamily: 'inherit',
  fontSize: '12.5px',
  fontWeight: 600,
  borderRadius: '9px',
  padding: '9px 16px',
  cursor: 'pointer',
  ':hover': primary
    ? { filter: 'brightness(1.06)' }
    : { borderColor: 'var(--accent-border)' },
});

const pagerButtonSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  border: '1px solid var(--border)',
  background: 'var(--panel)',
  color: 'var(--text)',
  fontFamily: 'inherit',
  fontSize: '12.5px',
  fontWeight: 600,
  borderRadius: '9px',
  padding: '10px 16px',
  cursor: 'pointer',
  ':hover': { borderColor: 'var(--accent-border)' },
};

const crumbChevron = (
  <DocIcon name="chevron_right" size={14} color="var(--text2)" />
);

export const ArticleView = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { locale, role, setRole, setNavOpen, courses } = useOutletContext();
  const config = useConfig();

  const courseSlug = searchParams.get('course');
  const course = courseSlug
    ? courses?.find((item) => item.slug === courseSlug)
    : null;
  const { markDone } = useCourseProgress(courseSlug || 'none');

  const mainRef = useRef(null);
  const articleRef = useRef(null);
  const [activeHeading, setActiveHeading] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [saved, setSaved] = useState(false);
  const [inPlan, setInPlan] = useState(false);
  const [shared, setShared] = useState(false);
  const [overviewOpen, setOverviewOpen] = useState(true);

  const { apiData, isLoading, serverError } = useFetch(
    `documentation/${slug}?locale=${locale}`
  );
  const entry = apiData?.data;

  useEffect(() => {
    setSaved(readList('documentation-bookmarks').includes(slug));
    setInPlan(readList('documentation-learnplan').includes(slug));
  }, [slug]);

  // Visiting an article as part of a course marks its lesson as done (client-side progress)
  useEffect(() => {
    if (courseSlug && entry) markDone(entry.slug);
  }, [courseSlug, entry]); // eslint-disable-line react-hooks/exhaustive-deps

  const topicSlug = entry?.topics?.[0]?.slug;
  const { apiData: topicEntriesData } = useFetch(
    entry && topicSlug && !courseSlug
      ? `documentation?locale=${locale}&topic=${topicSlug}`
      : null
  );

  const { html: articleContent, headings } = useMemo(
    () =>
      entry
        ? extractArticleOutline(
            sanitizeDocumentationHtml(
              resolveMediaPlaceholders(entry.htmlContent, normalizeMedia(entry))
            )
          )
        : { html: '', headings: [] },
    [entry]
  );

  useEffect(() => {
    setActiveHeading(headings[0]?.id ?? null);
  }, [headings]);

  useEffect(() => {
    setLightboxImage(null);
  }, [slug]);

  // The article html is injected via dangerouslySetInnerHTML, so the fullscreen
  // buttons on inline `article-image` images have to be added to the raw DOM
  useEffect(() => {
    if (!articleRef.current) return;
    articleRef.current
      .querySelectorAll('img.article-image')
      .forEach((image) => {
        if (image.parentElement?.classList.contains('article-image-frame'))
          return;
        const frame = document.createElement('span');
        frame.className = 'article-image-frame';
        image.replaceWith(frame);
        frame.appendChild(image);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'article-image-fullscreen';
        button.title = t('View image in fullscreen');
        button.setAttribute('aria-label', t('View image in fullscreen'));
        button.innerHTML =
          '<span class="material-symbols-outlined select-none" aria-hidden="true">fullscreen</span>';
        button.addEventListener('click', () =>
          setLightboxImage({
            src: image.currentSrc || image.src,
            alt: image.alt,
          })
        );
        frame.appendChild(button);
      });
  }, [articleContent, t]);

  // Videos embedded inline in the article html are raw DOM nodes (injected via
  // dangerouslySetInnerHTML), so watch-time tracking attaches to them directly —
  // same approach as the fullscreen buttons above. Their src is the resolved
  // signed URL, which maps back to the media entry (and its uuid).
  useEffect(() => {
    if (!articleRef.current || !entry) return;
    const mediaBySrc = new Map();
    normalizeMedia(entry)
      .filter(isTrackableVideo)
      .forEach((item) => {
        if (item.signedUrl) mediaBySrc.set(item.signedUrl, item);
      });
    const cleanups = [...articleRef.current.querySelectorAll('video')]
      .map((video) => {
        const item = mediaBySrc.get(video.getAttribute('src'));
        return (
          item &&
          attachWatchTracker(video, (report) =>
            sendWatchReport(config, item, report)
          )
        );
      })
      .filter(Boolean);
    return () => cleanups.forEach((cleanup) => cleanup());
  }, [articleContent, entry, config]);

  function handleScroll() {
    const main = mainRef.current;
    if (!main || !headings.length) return;
    const mainTop = main.getBoundingClientRect().top;
    let active = headings[0].id;
    for (const heading of headings) {
      const el = document.getElementById(heading.id);
      if (el && el.getBoundingClientRect().top - mainTop <= 90)
        active = heading.id;
    }
    setActiveHeading((prev) => (prev === active ? prev : active));
  }

  function scrollToHeading(id) {
    const main = mainRef.current;
    const el = document.getElementById(id);
    if (!main || !el) return;
    const offset =
      el.getBoundingClientRect().top - main.getBoundingClientRect().top;
    main.scrollTo({ top: main.scrollTop + offset - 24, behavior: 'smooth' });
  }

  if (serverError)
    return (
      <Box component="main" className="flex-1 min-w-0 h-full overflow-y-auto">
        <Box className="mx-auto text-center" sx={{ maxWidth: '860px', padding: '80px 40px' }}>
          <Box sx={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
            {t('This documentation entry could not be found.')}
          </Box>
          <Link to="/documentation">{t('Help Center')}</Link>
        </Box>
      </Box>
    );

  if (isLoading || !entry) return <ArticleSkeleton />;

  // Prev/next follow the course lessons when reading inside a course, else the topic order
  const sequence = course
    ? course.sections.flatMap((section) =>
        section.lessons.map((lesson) => lesson.entry)
      )
    : topicEntriesData?.data || [];
  const entryIndex = sequence.findIndex((item) => item.slug === entry.slug);
  const previousEntry = entryIndex > 0 ? sequence[entryIndex - 1] : null;
  const nextEntry =
    entryIndex >= 0 && entryIndex < sequence.length - 1
      ? sequence[entryIndex + 1]
      : null;

  const media = normalizeMedia(entry);
  const heroVideo = media.find((item) => item.mediaType === 'video');
  // Below the html only media that is neither embedded inline nor the intro video
  const inlineMediaIds = extractMediaPlaceholderIds(entry.htmlContent);
  const extraMedia = media.filter(
    (item) => item !== heroVideo && !inlineMediaIds.has(mediaUuid(item))
  );
  const extraVideos = extraMedia.filter((item) => item.mediaType === 'video');
  const images = extraMedia.filter((item) => item.mediaType === 'image');
  const files = extraMedia.filter(
    (item) => item.mediaType !== 'video' && item.mediaType !== 'image'
  );
  const updatedAt = new Date(entry.updatedAt).toLocaleDateString(
    locale === 'de' ? 'de-DE' : 'en-GB'
  );

  const openInSequence = (targetSlug) =>
    navigate(
      `/documentation/articles/${targetSlug}${courseSlug ? `?course=${courseSlug}` : ''}`
    );

  function shareLink() {
    try {
      navigator.clipboard.writeText(window.location.href);
    } catch (e) {}
    setShared(true);
    setTimeout(() => setShared(false), 2000);
  }

  return (
    <>
      <Box
        component="main"
        ref={mainRef}
        onScroll={handleScroll}
        className="flex-1 min-w-0 h-full overflow-y-auto"
      >
        <Box className="mx-auto" sx={{ maxWidth: '860px', padding: '28px 40px 80px' }}>
          <Box
            className="flex items-center flex-wrap"
            sx={{ gap: '8px', fontSize: '12px', color: 'var(--text2)' }}
          >
            <NavToggleButton onClick={() => setNavOpen(true)} />
            <Link to="/documentation">{t('Help Center')}</Link>
            {crumbChevron}
            {course ? (
              <>
                <Link to={`/documentation/courses/${course.slug}`}>
                  {course.title}
                </Link>
                {crumbChevron}
              </>
            ) : (
              entry.topics?.[0] && (
                <>
                  <span>{entry.topics[0].name}</span>
                  {crumbChevron}
                </>
              )
            )}
            <Box component="span" sx={{ color: 'var(--text)', fontWeight: 500 }}>
              {entry.title}
            </Box>
          </Box>

          <Box
            component="h1"
            sx={{ fontSize: '28px', fontWeight: 700, lineHeight: 1.25, margin: '18px 0 8px' }}
          >
            {entry.title}
          </Box>
          <Box
            className="flex items-center flex-wrap"
            sx={{ gap: '14px', fontSize: '12px', color: 'var(--text2)', marginBottom: '22px' }}
          >
            <Box component="span" className="inline-flex items-center" sx={{ gap: '5px' }}>
              <DocIcon
                name={entry.contentType === 'video' ? 'play_circle' : 'schedule'}
                size={15}
              />
              {formatEntryMeta(entry, t)}
            </Box>
            <span>{t('Updated on {{date}}', { date: updatedAt })}</span>
            {entry.isBeta && (
              <Box
                component="span"
                sx={{
                  border: '1px solid var(--accent-border)',
                  color: 'var(--accent-strong)',
                  borderRadius: '99px',
                  padding: '2px 10px',
                  background: 'var(--accent-soft)',
                }}
              >
                Beta
              </Box>
            )}
            {entry.topics?.map((topic) => (
              <Box
                key={topic.slug}
                component="span"
                sx={{
                  border: '1px solid var(--border)',
                  borderRadius: '99px',
                  padding: '2px 10px',
                  background: 'var(--panel)',
                }}
              >
                {topic.name}
              </Box>
            ))}
          </Box>

          {entry.roles && entry.roles.length > 0 && (
            <Box
              className="flex items-center flex-wrap"
              sx={{
                gap: '12px',
                border: '1px solid var(--border)',
                borderRadius: '11px',
                background: 'var(--panel)',
                padding: '11px 16px',
                marginBottom: '24px',
              }}
            >
              <Box
                component="span"
                className="inline-flex items-center"
                sx={{ gap: '7px', fontSize: '12px', fontWeight: 600, color: 'var(--text2)' }}
              >
                <DocIcon name="group" size={17} />
                {t('Who is this entry for?')}
              </Box>
              <Box className="flex flex-wrap" sx={{ gap: '6px' }}>
                {entry.roles.map((item) => {
                  const selected = role === item;
                  return (
                    <Box
                      key={item}
                      component="button"
                      title={t('Show only entries for this role')}
                      onClick={() => setRole(selected ? 'all' : item)}
                      className="cursor-pointer"
                      sx={{
                        border: `1px solid ${selected ? 'var(--accent)' : 'var(--accent-border)'}`,
                        background: 'var(--accent-soft)',
                        color: 'var(--accent-strong)',
                        fontFamily: 'inherit',
                        fontSize: '11px',
                        fontWeight: 600,
                        borderRadius: '99px',
                        padding: '4px 12px',
                        ':hover': { borderColor: 'var(--accent)' },
                      }}
                    >
                      {labelForRole(item)}
                    </Box>
                  );
                })}
              </Box>
              <Box component="span" sx={{ fontSize: '11px', color: 'var(--text2)', flexBasis: '100%' }}>
                {t('Click a role to filter the navigation to relevant entries.')}
              </Box>
            </Box>
          )}

          {heroVideo && (
            <Box sx={{ marginBottom: '18px' }}>
              <DocumentationVideo media={heroVideo} />
            </Box>
          )}

          <Box className="flex flex-wrap" sx={{ gap: '10px', margin: '0 0 26px' }}>
            {SHOW_LEARN_PLAN_AND_BOOKMARKS && (
              <>
                <Box
                  component="button"
                  onClick={() =>
                    setInPlan(toggleListEntry('documentation-learnplan', slug))
                  }
                  sx={actionButtonSx(true)}
                >
                  <DocIcon name={inPlan ? 'check' : 'playlist_add'} size={17} />
                  {inPlan ? t('In learn plan') : t('Add to learn plan')}
                </Box>
                <Box
                  component="button"
                  onClick={() =>
                    setSaved(toggleListEntry('documentation-bookmarks', slug))
                  }
                  sx={actionButtonSx(false)}
                >
                  <DocIcon
                    name={saved ? 'bookmark' : 'bookmark_border'}
                    size={17}
                    color="var(--accent)"
                  />
                  {saved ? t('Bookmarked') : t('Bookmark')}
                </Box>
              </>
            )}
            <Box component="button" onClick={shareLink} sx={actionButtonSx(false)}>
              <DocIcon name="link" size={17} color="var(--accent)" />
              {shared ? t('Link copied') : t('Share link')}
            </Box>
          </Box>

          {headings.length > 0 && (
            <Box
              sx={{
                border: '1px solid var(--border)',
                borderRadius: '12px',
                background: 'var(--panel)',
                marginBottom: '30px',
              }}
            >
              <Box
                component="button"
                onClick={() => setOverviewOpen((prev) => !prev)}
                className="w-full flex items-center cursor-pointer text-left"
                sx={{
                  gap: '10px',
                  padding: '13px 16px',
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--text)',
                  fontFamily: 'inherit',
                  fontSize: '13px',
                  fontWeight: 600,
                }}
              >
                <DocIcon name="list_alt" size={19} color="var(--text2)" />
                <Box component="span" className="flex-1">
                  {t('Contents')}
                </Box>
                <DocIcon
                  name={overviewOpen ? 'expand_less' : 'expand_more'}
                  size={19}
                  color="var(--text2)"
                />
              </Box>
              {overviewOpen && (
                <Box
                  component="ol"
                  sx={{
                    margin: 0,
                    padding: '0 16px 14px 46px',
                    fontSize: '12.5px',
                    lineHeight: 2,
                  }}
                >
                  {groupOutline(headings).map((section) => (
                    <Box
                      key={section.id}
                      component="li"
                      sx={{ color: 'var(--text2)' }}
                    >
                      <Box
                        component="span"
                        className="cursor-pointer"
                        sx={{
                          fontWeight: 600,
                          color: 'var(--text)',
                          ':hover': { color: 'var(--accent-strong)' },
                        }}
                        onClick={() => scrollToHeading(section.id)}
                      >
                        {section.text}
                      </Box>
                      {section.children.length > 0 && (
                        <Box
                          component="ul"
                          sx={{
                            listStyle: 'none',
                            margin: 0,
                            padding: '0 0 0 2px',
                          }}
                        >
                          {section.children.map((sub) => (
                            <Box
                              key={sub.id}
                              component="li"
                              className="cursor-pointer flex items-baseline"
                              sx={{
                                gap: '8px',
                                fontSize: '12px',
                                color: 'var(--text2)',
                                ':hover': { color: 'var(--accent-strong)' },
                              }}
                              onClick={() => scrollToHeading(sub.id)}
                            >
                              <Box
                                component="span"
                                aria-hidden
                                sx={{ color: 'var(--border)' }}
                              >
                                –
                              </Box>
                              {sub.text}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          <Box
            component="article"
            ref={articleRef}
            sx={{
              fontSize: '14px',
              lineHeight: 1.75,
              '& .article-image-frame': {
                position: 'relative',
                display: 'inline-block',
                maxWidth: '100%',
              },
              '& .article-image-fullscreen': {
                position: 'absolute',
                top: '8px',
                right: '8px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid var(--border)',
                background: 'var(--panel)',
                color: 'var(--text)',
                borderRadius: '8px',
                padding: '5px',
                cursor: 'pointer',
                opacity: 0.85,
                ':hover': { opacity: 1, borderColor: 'var(--accent-border)' },
                '& .material-symbols-outlined': {
                  fontSize: '17px',
                  lineHeight: 1,
                },
              },
              '& h1': { fontSize: '22px', fontWeight: 700, margin: '0 0 12px', scrollMarginTop: '20px' },
              '& h2': { fontSize: '20px', fontWeight: 700, margin: '34px 0 12px', scrollMarginTop: '20px' },
              '& h3': { fontSize: '16px', fontWeight: 700, margin: '26px 0 10px', scrollMarginTop: '20px' },
              '& h1:first-of-type, & h2:first-of-type': { marginTop: 0 },
              '& p': { margin: '0 0 16px', textWrap: 'pretty' },
              // Tailwind's preflight resets list-style to none — restore the markers
              '& ul, & ol': { margin: '0 0 20px', paddingLeft: '22px', lineHeight: 2 },
              '& ol': { listStyle: 'decimal' },
              '& ul': { listStyle: 'disc' },
              '& a': { color: 'var(--accent-strong)' },
              '& img': { maxWidth: '100%', borderRadius: '10px', border: '1px solid var(--border)' },
              '& figure': { margin: '0 0 18px' },
              '& figcaption': { fontSize: '11.5px', color: 'var(--text2)', marginTop: '7px' },
              '& pre': {
                margin: '0 0 18px',
                padding: '14px 16px',
                background: 'var(--code-bg)',
                color: '#d5dce8',
                fontSize: '12.5px',
                lineHeight: 1.7,
                overflowX: 'auto',
                borderRadius: '10px',
              },
              '& code': { background: 'var(--panel2)', borderRadius: '4px', padding: '1px 5px' },
              '& pre code': { background: 'transparent', padding: 0 },
              '& kbd': {
                fontSize: '11px',
                fontWeight: 600,
                border: '1px solid var(--border)',
                borderRadius: '5px',
                padding: '1px 6px',
                background: 'var(--panel)',
              },
              '& table': { borderCollapse: 'collapse', width: '100%', marginBottom: '16px' },
              '& th, & td': {
                border: '1px solid var(--border)',
                padding: '8px 10px',
                textAlign: 'left',
              },
              '& blockquote': {
                display: 'block',
                border: '1px solid var(--accent-border)',
                background: 'var(--accent-soft)',
                borderRadius: '10px',
                padding: '13px 16px',
                margin: '0 0 20px',
                fontSize: '13px',
              },
              // Documentation-server content classes ("ExoPaper Help Center" design),
              // mapped onto the help theme vars so they follow dark mode
              '& .callout': {
                display: 'block',
                background: 'var(--panel2)',
                borderLeft: '3px solid var(--text2)',
                borderRadius: '0 10px 10px 0',
                padding: '12px 16px',
                margin: '0 0 20px',
                color: 'var(--text2)',
              },
              '& .callout strong': { color: 'var(--text)' },
              '& .callout p:last-child': { marginBottom: 0 },
              '& .callout--tip': { borderLeftColor: 'var(--green)' },
              '& .callout--info': { borderLeftColor: 'var(--accent)' },
              '& .callout--warning': { borderLeftColor: 'var(--amber)' },
              '& .ui-label': {
                fontWeight: 600,
                background: 'var(--panel2)',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                padding: '0.05em 0.4em',
                whiteSpace: 'nowrap',
              },
            }}
            dangerouslySetInnerHTML={{ __html: articleContent }}
          />

          {(extraVideos.length > 0 || images.length > 0 || files.length > 0) && (
            <Box className="flex flex-col" sx={{ gap: '18px', marginTop: '26px' }}>
              {extraVideos.map((item) => (
                <DocumentationVideo key={item.url} media={item} />
              ))}
              {images.map((item) => (
                <DocumentationImage key={item.url} media={item} />
              ))}
              {files.map((item) => (
                <DocumentationFileLink key={item.url} media={item} />
              ))}
            </Box>
          )}

          {(previousEntry || nextEntry) && (
            <Box
              className="flex justify-between"
              sx={{
                gap: '12px',
                borderTop: '1px solid var(--border)',
                paddingTop: '20px',
                marginTop: '36px',
              }}
            >
              {previousEntry ? (
                <Box
                  component="button"
                  onClick={() => openInSequence(previousEntry.slug)}
                  sx={{ ...pagerButtonSx, textAlign: 'left' }}
                >
                  <DocIcon name="arrow_back" size={17} color="var(--text2)" />
                  <span>
                    <Box
                      component="span"
                      className="block"
                      sx={{ fontSize: '10px', color: 'var(--text2)', fontWeight: 500 }}
                    >
                      {t('Previous article')}
                    </Box>
                    {previousEntry.title}
                  </span>
                </Box>
              ) : (
                <span />
              )}
              {nextEntry && (
                <Box
                  component="button"
                  onClick={() => openInSequence(nextEntry.slug)}
                  sx={{ ...pagerButtonSx, textAlign: 'right' }}
                >
                  <span>
                    <Box
                      component="span"
                      className="block"
                      sx={{ fontSize: '10px', color: 'var(--text2)', fontWeight: 500 }}
                    >
                      {t('Next article')}
                    </Box>
                    {nextEntry.title}
                  </span>
                  <DocIcon name="arrow_forward" size={17} color="var(--text2)" />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {lightboxImage && (
        <ImageLightbox
          src={lightboxImage.src}
          alt={lightboxImage.alt}
          onClose={() => setLightboxImage(null)}
        />
      )}

      <Box
        component="aside"
        className="hidden xl:block flex-none h-full overflow-y-auto"
        sx={{
          width: '250px',
          padding: '30px 22px',
          borderLeft: '1px solid var(--border)',
        }}
      >
        <Toc headings={headings} activeId={activeHeading} onGo={scrollToHeading} />
        <Box sx={{ marginTop: headings.length ? '28px' : 0 }}>
          <FeedbackWidget slug={entry.slug} />
        </Box>
      </Box>
    </>
  );
};
